import type { DisplayMode } from '@/types/book';
import type { ImageLoadFailureKind } from '@/types/canvas-type';
import type { BookPage } from '@/modules/editor/canvas/book';
import type { BookCore } from './BookCore';
import { getReaderResidentPageIndexes, getReaderSpreadPageIndexes } from './spreadPageIndexes';
import { getRetryablePageFailureIndexes } from './retryablePageFailures';
import { resolveReaderPageVisualState } from './readerPageVisualState';

/** 阅读端页宿主：提供唯一 BookPage 与时间轴刷新，由加载管理器驱动。 */
export interface ReaderPageHost {
  getPageCount(): number;
  getPageByIndex(index: number): BookPage | null;
  /** 页内容 load + initTween 完成后刷新可播放列表。 */
  onPageContentReady(): void;
  /** 页内容 unload 后刷新可播放列表。 */
  onPageContentUnloaded(): void;
}

interface ReaderPageLoadEntry {
  /** 已成功挂到翻页层的 pageMap 页。 */
  page?: BookPage;
  promise?: Promise<void>;
  /** 加载失败后保留原因，避免同轮 sync 反复重试；unload 或主动重试后清除。 */
  failureKind?: ImageLoadFailureKind;
  token: number;
  controller: AbortController;
}

interface ReaderPageLoadManagerOptions {
  displayMode: DisplayMode;
}

/**
 * 阅读端统一页资源加载管理器：按可视窗口 load/unload 唯一 BookPage
 *（插画 + 旁白/动画一次完成），挂到翻页层并刷新时间轴 PlayAction。
 */
export class ReaderPageLoadManager {
  private readonly book: BookCore;
  private readonly host: ReaderPageHost;
  private displayMode: DisplayMode;
  private readonly entries = new Map<number, ReaderPageLoadEntry>();
  private tokenSeed = 0;
  private disposed = false;
  private lastSyncedPageIndex: number | null = null;
  private syncVersion = 0;

  constructor(book: BookCore, host: ReaderPageHost, options: ReaderPageLoadManagerOptions) {
    this.book = book;
    this.host = host;
    this.displayMode = options.displayMode;
  }

  /**
   * 步骤 1：按当前页推进可视窗口。
   * 步骤 2：窗外 unload；窗内启动 load（不 await）。
   */
  sync(currentPageIndex: number): void {
    if (this.disposed) return;
    if (this.lastSyncedPageIndex === currentPageIndex) return;
    this.lastSyncedPageIndex = currentPageIndex;
    const version = ++this.syncVersion;

    const orderedPageIndexes = this.getVisiblePageIndexes(currentPageIndex);
    const wantedPageIndexes = new Set(orderedPageIndexes);

    for (const index of Array.from(this.entries.keys())) {
      if (!wantedPageIndexes.has(index)) {
        this.unloadPage(index);
      }
    }

    const spreadPageIndexes = this.getSpreadIndexes(currentPageIndex).filter((index) =>
      wantedPageIndexes.has(index)
    );
    const spreadPageIndexSet = new Set(spreadPageIndexes);
    const backgroundPageIndexes = orderedPageIndexes.filter(
      (index) => !spreadPageIndexSet.has(index)
    );

    spreadPageIndexes.forEach((index) => this.loadPage(index));
    const firstBatchLoads = this.getPendingLoads(spreadPageIndexes);
    void Promise.allSettled(firstBatchLoads)
      .then(async () => {
        if (
          this.disposed ||
          version !== this.syncVersion ||
          this.lastSyncedPageIndex !== currentPageIndex
        )
          return;

        backgroundPageIndexes.forEach((index) => this.loadPage(index));
        const backgroundLoads = this.getPendingLoads(backgroundPageIndexes);
        if (backgroundLoads.length > 0) {
          await Promise.allSettled(backgroundLoads);
        }
      })
      .catch((error) => console.error(error));
  }

  /** 返回阅读端常驻窗口内的页索引，顺序即加载优先级。 */
  getVisiblePageIndexes(currentPageIndex: number): number[] {
    return getReaderResidentPageIndexes(
      currentPageIndex,
      this.displayMode,
      this.host.getPageCount()
    );
  }

  /**
   * 当前跨页（正在播放/展示的页）是否已就绪。
   * 前方预加载页未完成不影响此判断，避免自动播为等后方页而顿一下。
   */
  isSpreadReady(currentPageIndex: number): boolean {
    if (this.disposed) return false;

    for (const index of this.getSpreadIndexes(currentPageIndex)) {
      const entry = this.entries.get(index);
      if (!entry?.page || entry.promise) return false;
    }
    return true;
  }

  /**
   * 推进完整预加载窗口，但只等待「当前跨页」就绪。
   * 后方页继续后台加载，不挡当前播放。
   */
  async waitForSpread(currentPageIndex: number): Promise<void> {
    if (this.disposed) return;

    this.lastSyncedPageIndex = null;
    this.sync(currentPageIndex);

    const waits: Promise<void>[] = [];
    for (const index of this.getSpreadIndexes(currentPageIndex)) {
      const entry = this.entries.get(index);
      if (entry?.promise) waits.push(entry.promise);
    }

    if (waits.length > 0) {
      await Promise.all(waits);
    }
  }

  /** 批量重试当前常驻滑窗内所有网络失败页，永久失败页保持原状。 */
  retryVisibleFailures(): void {
    if (this.disposed) return;

    const currentPageIndex = this.lastSyncedPageIndex ?? this.book.currentPageIndex;
    const retryIndexes = getRetryablePageFailureIndexes(
      this.getVisiblePageIndexes(currentPageIndex),
      (index) => this.entries.get(index)?.failureKind
    );
    if (retryIndexes.length === 0) return;

    for (const index of retryIndexes) {
      const entry = this.entries.get(index);
      if (!entry || entry.failureKind !== 'network') continue;

      entry.controller.abort();
      this.entries.delete(index);
      const pixiPage = this.book.getPage(index);
      pixiPage?.content.removeChildren();
      pixiPage?.hideLoading();
      pixiPage?.hideError();
    }

    this.lastSyncedPageIndex = null;
    this.sync(currentPageIndex);
  }

  /**
   * 原地更新页面几何与已挂载内容位置，并按新的显示模式同步常驻窗口。
   */
  async resizePages(displayMode: DisplayMode, updateGeometry: () => boolean): Promise<boolean> {
    if (this.disposed) return false;

    if (!updateGeometry()) return false;
    this.displayMode = displayMode;
    for (const [index, entry] of this.entries) {
      const pixiPage = this.book.getPage(index);
      if (!pixiPage) continue;

      const visualState = resolveReaderPageVisualState({
        hasPage: Boolean(entry.page),
        hasPendingLoad: Boolean(entry.promise),
        failureKind: entry.failureKind
      });

      if (visualState === 'loaded' && entry.page) {
        pixiPage.hideLoading();
        pixiPage.hideError();
        entry.page.position.set(entry.page.pageWidth / 2, entry.page.pageHeight / 2);
        continue;
      }
      if (visualState === 'loading') {
        pixiPage.showLoading();
        continue;
      }
      if (visualState === 'network-error') {
        pixiPage.showError('network');
        continue;
      }
      if (visualState === 'permanent-error') {
        pixiPage.showError('permanent');
        continue;
      }

      pixiPage.hideLoading();
      pixiPage.hideError();
    }
    if (this.disposed) return false;
    this.book.refresh();
    this.lastSyncedPageIndex = null;
    await this.waitForSpread(this.book.currentPageIndex);
    return !this.disposed;
  }

  dispose(): void {
    this.disposed = true;
    this.syncVersion += 1;

    for (const index of Array.from(this.entries.keys())) {
      this.unloadPage(index);
    }
    this.entries.clear();
  }

  private getSpreadIndexes(currentPageIndex: number): number[] {
    return getReaderSpreadPageIndexes(currentPageIndex, this.displayMode, this.host.getPageCount());
  }

  private getPendingLoads(pageIndexes: number[]): Promise<void>[] {
    return pageIndexes
      .map((index) => this.entries.get(index)?.promise)
      .filter((promise): promise is Promise<void> => promise !== undefined);
  }

  /** 启动单页加载：使用 pageMap 唯一实例，禁止再 new BookPage。 */
  private loadPage(index: number): void {
    const pageCount = this.host.getPageCount();
    if (index < 0 || index >= pageCount) return;

    const existing = this.entries.get(index);
    if (existing?.page || existing?.promise || existing?.failureKind) return;

    const page = this.host.getPageByIndex(index);
    if (!page?.data) return;

    const controller = new AbortController();
    const token = ++this.tokenSeed;
    const entry: ReaderPageLoadEntry = { token, controller };
    this.entries.set(index, entry);

    this.book.getPage(index)?.showLoading();
    this.book.getPage(index)?.hideError();

    entry.promise = this.loadAndAttachPage(index, page, token, controller.signal);
  }

  /**
   * 步骤 1：load 页内容（含旁白）。
   * 步骤 2：initTween 并挂到翻页层。
   * 步骤 3：通知 host 刷新时间轴可播放列表。
   */
  private async loadAndAttachPage(
    index: number,
    page: BookPage,
    token: number,
    signal: AbortSignal
  ): Promise<void> {
    let result: Awaited<ReturnType<BookPage['load']>>;
    try {
      result = await page.load(signal);
    } catch (error) {
      console.error(error);
      result = { status: 'failed', failureKind: 'permanent' };
    }

    const entry = this.entries.get(index);
    if (
      this.disposed ||
      !entry ||
      entry.token !== token ||
      signal.aborted ||
      result.status === 'aborted'
    ) {
      // 过期/取消的加载可能留下半成品 Action；若无更新任务占用该页则清理，不 destroy 页壳
      const current = this.entries.get(index);
      if (!current || current.token === token) {
        page.unload();
      }
      return;
    }

    if (result.status === 'failed') {
      page.unload();
      this.book.getPage(index)?.showError(result.failureKind);
      entry.failureKind = result.failureKind;
      entry.promise = undefined;
      return;
    }

    page.updateMode('view');
    page.position.set(page.pageWidth / 2, page.pageHeight / 2);
    page.initTween();

    this.attachPage(index, page);
    this.book.getPage(index)?.hideLoading();
    this.book.getPage(index)?.hideError();
    entry.page = page;
    entry.promise = undefined;

    this.host.onPageContentReady();
  }

  /** 把已加载的 pageMap 页挂到对应 PixiPage。 */
  private attachPage(index: number, page: BookPage): void {
    const pixiPage = this.book.getPage(index);
    if (!pixiPage) {
      this.entries.delete(index);
      return;
    }

    pixiPage.content.removeChildren();
    pixiPage.content.addChild(page);
    this.book.refresh();
  }

  /**
   * 窗外卸载：卸翻页挂载 + page.unload 释放媒体，保留 pageMap 页壳与时间轴占位。
   */
  private unloadPage(index: number): void {
    const entry = this.entries.get(index);
    if (!entry) return;

    this.entries.delete(index);
    const pixiPage = this.book.getPage(index);
    pixiPage?.content.removeChildren();
    pixiPage?.hideLoading();
    pixiPage?.hideError();

    if (entry.page) {
      entry.page.unload();
      this.host.onPageContentUnloaded();
    } else {
      entry.controller.abort();
    }

    this.book.refresh();
  }
}
